import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { usePQC } from '../hooks/usePQC';
import { useAuth } from '../contexts/AuthContext';
import PQCKeyManager from './PQCKeyManager';
import PQCCryptoInterface from './PQCCryptoInterface';
import PQCSignatureManager from './PQCSignatureManager';
import { 
  Shield, 
  Key, 
  Lock, 
  FileText, 
  Activity, 
  Settings,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Info,
  Zap,
  Clock,
  TrendingUp,
  Users,
  Database
} from 'lucide-react';
import { toast } from 'sonner';

interface PQCSecurityCenterProps {
  className?: string;
}

type TabType = 'overview' | 'keys' | 'crypto' | 'signatures' | 'logs';

const PQCSecurityCenter: React.FC<PQCSecurityCenterProps> = ({ className }) => {
  const { user } = useAuth();
  const {
    isInitialized,
    hasValidKeys,
    securityStatus,
    securityLogs,
    getKeyMetrics,
    getSecurityStatus
  } = usePQC();

  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const keyMetrics = getKeyMetrics();
  const currentSecurityStatus = getSecurityStatus();

  const getStatusColor = (level: string) => {
    switch (level) {
      case 'high': return 'text-green-600 bg-green-100 border-green-200';
      case 'medium': return 'text-yellow-600 bg-yellow-100 border-yellow-200';
      case 'low': return 'text-orange-600 bg-orange-100 border-orange-200';
      case 'critical': return 'text-red-600 bg-red-100 border-red-200';
      default: return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const getStatusIcon = (level: string) => {
    switch (level) {
      case 'high': return <CheckCircle className="h-5 w-5" />;
      case 'medium': return <AlertTriangle className="h-5 w-5" />;
      case 'low': return <Clock className="h-5 w-5" />;
      case 'critical': return <XCircle className="h-5 w-5" />;
      default: return <Shield className="h-5 w-5" />;
    }
  };

  const tabs = [
    {
      id: 'overview' as TabType,
      label: 'Visão Geral',
      icon: <Activity className="h-4 w-4" />,
      description: 'Status e métricas de segurança'
    },
    {
      id: 'keys' as TabType,
      label: 'Chaves',
      icon: <Key className="h-4 w-4" />,
      description: 'Gerenciamento de chaves PQC'
    },
    {
      id: 'crypto' as TabType,
      label: 'Criptografia',
      icon: <Lock className="h-4 w-4" />,
      description: 'Criptografar e descriptografar dados'
    },
    {
      id: 'signatures' as TabType,
      label: 'Assinaturas',
      icon: <FileText className="h-4 w-4" />,
      description: 'Assinar e verificar documentos'
    },
    {
      id: 'logs' as TabType,
      label: 'Logs',
      icon: <Database className="h-4 w-4" />,
      description: 'Histórico de atividades'
    }
  ];

  if (!user) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="text-center py-8">
            <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Centro de Segurança PQC</h3>
            <p className="text-gray-500">Faça login para acessar as funcionalidades de criptografia pós-quântica</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Status Principal */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Status de Segurança PQC
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className={`flex items-center justify-between p-4 rounded-lg border ${getStatusColor(securityStatus.level)}`}>
            <div className="flex items-center gap-3">
              {getStatusIcon(securityStatus.level)}
              <div>
                <p className="font-medium">{securityStatus.message}</p>
                <p className="text-sm opacity-75">
                  Última verificação: {new Date(securityStatus.lastCheck).toLocaleString('pt-BR')}
                </p>
              </div>
            </div>
            <Badge className={getStatusColor(securityStatus.level)}>
              {securityStatus.level.toUpperCase()}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Métricas Rápidas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Chaves Ativas</p>
                <p className="text-2xl font-bold">{keyMetrics.totalKeys}</p>
              </div>
              <Key className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Operações Hoje</p>
                <p className="text-2xl font-bold">{keyMetrics.operationsToday}</p>
              </div>
              <Activity className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Última Rotação</p>
                <p className="text-sm font-bold">
                  {keyMetrics.lastRotation ? 
                    new Date(keyMetrics.lastRotation).toLocaleDateString('pt-BR') : 
                    'Nunca'
                  }
                </p>
              </div>
              <Clock className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Nível de Segurança</p>
                <p className="text-sm font-bold capitalize">{securityStatus.level}</p>
              </div>
              <Shield className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recomendações */}
      {currentSecurityStatus.recommendations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Recomendações de Segurança
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {currentSecurityStatus.recommendations.map((rec, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                  <Info className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-900">{rec}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Atividades Recentes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Atividades Recentes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {securityLogs.length > 0 ? (
            <div className="space-y-3">
              {securityLogs.slice(0, 5).map((log, index) => (
                <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    <div>
                      <p className="text-sm font-medium">{log.action}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(log.timestamp).toLocaleString('pt-BR')}
                      </p>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {log.type}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Database className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Nenhuma atividade registrada</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const renderLogs = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="h-5 w-5" />
          Logs de Segurança
        </CardTitle>
        <CardDescription>
          Histórico completo de atividades de criptografia pós-quântica
        </CardDescription>
      </CardHeader>
      <CardContent>
        {securityLogs.length > 0 ? (
          <div className="space-y-4">
            {securityLogs.map((log, index) => (
              <div key={index} className="flex items-start gap-4 p-4 border rounded-lg hover:bg-gray-50">
                <div className="w-3 h-3 bg-blue-600 rounded-full mt-2"></div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium">{log.action}</h4>
                    <Badge variant="outline" className="text-xs">
                      {log.type}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{log.details}</p>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>{new Date(log.timestamp).toLocaleString('pt-BR')}</span>
                    {log.userId && <span>Usuário: {log.userId}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Database className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum log encontrado</h3>
            <p className="text-gray-500">As atividades de segurança aparecerão aqui quando realizadas</p>
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-6 w-6" />
            Centro de Segurança PQC
          </CardTitle>
          <CardDescription>
            Gerenciamento completo de criptografia pós-quântica para proteção de dados médicos
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Navigation Tabs */}
      <Card>
        <CardContent className="p-0">
          <div className="border-b">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {tab.icon}
                    <span>{tab.label}</span>
                  </div>
                </button>
              ))}
            </nav>
          </div>
        </CardContent>
      </Card>

      {/* Tab Content */}
      <div className="min-h-[600px]">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'keys' && <PQCKeyManager />}
        {activeTab === 'crypto' && <PQCCryptoInterface />}
        {activeTab === 'signatures' && <PQCSignatureManager />}
        {activeTab === 'logs' && renderLogs()}
      </div>

      {/* Quick Actions */}
      {!hasValidKeys() && (
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-orange-600" />
                <div>
                  <p className="font-medium text-orange-900">Configuração Necessária</p>
                  <p className="text-sm text-orange-700">
                    Você precisa gerar suas chaves PQC para usar as funcionalidades de segurança
                  </p>
                </div>
              </div>
              <Button
                onClick={() => setActiveTab('keys')}
                className="bg-orange-600 hover:bg-orange-700"
              >
                Configurar Agora
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PQCSecurityCenter;